import { useEffect, useMemo, useState } from 'react';

import LayerSelector from '@App/components/LayerSelector/LayerSelector';
import DatePicker from '@App/components/DatePicker/DatePicker';
import InfoIcon from '@App/components/InfoIcon/InfoIcon';
import LayerControl from '@App/components/MaplibreMap/LayerControl/LayerControl';
import SearchControl from '@App/components/MaplibreMap/SearchControl/SearchControl';
import CaseStudyMapCard from '@App/pages/CaseStudy/CaseStudyMapCard/CaseStudyMapCard';
import MapContainer from '@App/components/MapContainer/MapContainer';
import MapCard from '@App/components/MapCard/MapCard';
import { useTranslation } from '@utils/i18n/i18n';
import { useDataLayerResources } from '@services/query.service';

import { LOCATION } from '../Context/context.constants';

import WaterQualityLayer from './WaterQualityLayer/WaterQualityLayer';
import MeasurementStationsLayer from './MeasurementStationsLayer/MeasurementStationsLayer';
import MicrobialContaminationLayer from './MicrobialContaminationLayer/MicrobialContaminationLayer';
import { getAvailableDatesByLayer } from './WaterQualityLayer/water-quality-layer.utils';

import './map.css';

function Map() {
  const { t } = useTranslation();
  const [selectedWaterQualityLayer, setSelectedWaterQualityLayer] = useState('turbidity');
  const [selectedDate, setSelectedDate] = useState(null);
  const [showMeasurementStations, setShowMeasurementStations] = useState(false);
  const [showMicrobialContaminationLayer, setShowMicrobialContaminationLayer] = useState(false);

  const { data: resources = [] } = useDataLayerResources();
  const availableDatesByLayer = useMemo(() => getAvailableDatesByLayer(resources), [resources]);
  console.log('availableDatesByLayer', availableDatesByLayer);
  const { dates: availableDates = [], resourceIdByDate = {} } = (
    availableDatesByLayer[selectedWaterQualityLayer] || {}
  );
  const selectedResourceId = selectedDate ? resourceIdByDate[selectedDate] : undefined;

  // Whenever the selected layer changes (or the available dates finish loading), make
  // sure the selected date is still valid for that layer, falling back to its latest date.
  useEffect(() => {
    if (availableDates.length === 0) {
      return;
    }

    if (!selectedDate || !availableDates.includes(selectedDate)) {
      setSelectedDate(availableDates[0]);
    }
  }, [availableDates, selectedDate]);

  return (
    <CaseStudyMapCard
      className="case-study__section pagasitikos-gulf-map"
      initialCenter={LOCATION.coordinates}
      initialZoom={9}
      label={t('Map')}
    >
      <WaterQualityLayer
        selectedLayer={selectedWaterQualityLayer}
        selectedResourceId={selectedResourceId}
      />
      {showMeasurementStations && <MeasurementStationsLayer />}
      {showMicrobialContaminationLayer && <MicrobialContaminationLayer />}
      <MapContainer position="top-left">
        <MapCard className="pagasitikos-gulf-map__layer-selector">
          <LayerSelector
            title={t('Layers')}
            groups={[
              {
                title: t('Water quality'),
                layers: [
                  {
                    label: (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '9px' }}>
                        {t('Turbidity')}
                        {' '}
                        <InfoIcon id="turbidity-info" tooltip="Turbidity tooltip info" />
                      </div>
                    ),
                    value: 'turbidity',
                  },
                  {
                    label: (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '9px' }}>
                        {t('Chlorophyll a')}
                        {' '}
                        <InfoIcon id="chlorophyll-info" tooltip="Chlorophyll tooltip info" />
                      </div>
                    ),
                    value: 'chlorophyll-a',
                  },
                ],
                selectedLayer: selectedWaterQualityLayer,
                onChange: e => setSelectedWaterQualityLayer(e.target.value),
              },
            ]}
            singularLayers={[
              {
                id: 'measurement-stations-layer',
                label: t('Measurement stations'),
                checked: showMeasurementStations,
                onChange: e => setShowMeasurementStations(e.target.checked),
              },
              {
                id: 'microbial-contamination-layer',
                label: t('Microbial contamination'),
                checked: showMicrobialContaminationLayer,
                onChange: e => setShowMicrobialContaminationLayer(e.target.checked),
              },
            ]}
          />
        </MapCard>
      </MapContainer>
      <MapContainer position="bottom-left">
        <MapCard className="pagasitikos-gulf-map__date-picker">
          <DatePicker
            value={selectedDate}
            availableDays={availableDates}
            onChange={setSelectedDate}
          />
        </MapCard>
      </MapContainer>
      <LayerControl />
      <SearchControl />
    </CaseStudyMapCard>
  );
}

export default Map;
